import React, { useState } from 'react';
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import ReplyIcon from '@mui/icons-material/Reply';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import CheckIcon from '@mui/icons-material/Check';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import DownloadIcon from '@mui/icons-material/Download';
import AddReactionOutlinedIcon from '@mui/icons-material/AddReactionOutlined';
import type { TeamChatMessage } from '../../../services/teamChatService';
import {
  TEAM_CHAT_THEME,
  teamChatBubbleBodySx,
  teamChatBubbleMetaSx,
  teamChatMessageBubbleSx,
  teamChatMessageBubbleWrapSx,
  teamChatQuoteSx,
  teamChatReactionChipSx,
  teamChatSystemMessageSx,
} from '../teamChatTheme';
import { QUICK_REACTIONS, formatFileSize, formatMessageTime } from '../teamChatUtils';

interface TeamChatMessageBubbleProps {
  message: TeamChatMessage;
  mine: boolean;
  showAuthor: boolean;
  readByAll: boolean;
  currentUserId: string;
  attachmentUrl: string;
  onReply: (message: TeamChatMessage) => void;
  onEdit: (message: TeamChatMessage) => void;
  onDelete: (message: TeamChatMessage) => void;
  onToggleReaction: (message: TeamChatMessage, emoji: string) => void;
}

/**
 * Bolha de mensagem — wrapper com maxWidth 70% da linha + Paper (igual inbox dinâmico).
 */
const TeamChatMessageBubble: React.FC<TeamChatMessageBubbleProps> = ({
  message,
  mine,
  showAuthor,
  readByAll,
  currentUserId,
  attachmentUrl,
  onReply,
  onEdit,
  onDelete,
  onToggleReaction,
}) => {
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [reactionAnchor, setReactionAnchor] = useState<HTMLElement | null>(null);

  if (message.message_type === 'system') {
    return <Box sx={teamChatSystemMessageSx}>{message.body}</Box>;
  }

  const canEdit = mine && !message.is_deleted && message.message_type === 'text';
  const isImage = message.message_type === 'image' && attachmentUrl && !message.is_deleted;
  const isAudio = message.message_type === 'audio' && attachmentUrl && !message.is_deleted;
  const isFile = message.message_type === 'file' && attachmentUrl && !message.is_deleted;

  return (
    <Box sx={teamChatMessageBubbleWrapSx}>
      {showAuthor && !mine && (
        <Typography
          sx={{
            fontSize: '0.72rem',
            fontWeight: 700,
            color: TEAM_CHAT_THEME.primary,
            mb: 0.35,
            px: 0.5,
          }}
        >
          {message.sender?.full_name || 'Usuário'}
        </Typography>
      )}

      <Paper elevation={0} sx={teamChatMessageBubbleSx(mine)}>
        {message.reply_to && (
          <Box sx={teamChatQuoteSx(mine)}>
            <Typography
              sx={{
                fontSize: '0.68rem',
                fontWeight: 700,
                color: mine ? 'rgba(248,250,255,0.9)' : TEAM_CHAT_THEME.primary,
              }}
            >
              {message.reply_to.sender_name || 'Sistema'}
            </Typography>
            <Typography
              noWrap
              sx={{
                fontSize: '0.74rem',
                color: mine ? 'rgba(248,250,255,0.78)' : TEAM_CHAT_THEME.slateMid,
                maxWidth: 260,
              }}
            >
              {message.reply_to.is_deleted
                ? 'Mensagem apagada'
                : message.reply_to.body ||
                  (message.reply_to.attachment_name ? `📎 ${message.reply_to.attachment_name}` : 'Anexo')}
            </Typography>
          </Box>
        )}

        {message.is_deleted ? (
          <Typography sx={{ ...teamChatBubbleBodySx, fontStyle: 'italic', opacity: 0.7 }}>
            Mensagem apagada
          </Typography>
        ) : (
          <>
            {isImage && (
              <Box
                component="a"
                href={attachmentUrl}
                target="_blank"
                rel="noreferrer"
                sx={{ display: 'block', mb: message.body ? 0.75 : 0 }}
              >
                <Box
                  component="img"
                  src={attachmentUrl}
                  alt={message.attachment_name || 'Imagem'}
                  sx={{
                    display: 'block',
                    maxWidth: 320,
                    width: '100%',
                    borderRadius: 1.5,
                    cursor: 'zoom-in',
                  }}
                />
              </Box>
            )}

            {isAudio && (
              <Box
                component="audio"
                controls
                preload="metadata"
                src={attachmentUrl}
                sx={{ width: 260, maxWidth: '100%', mb: message.body ? 0.75 : 0, display: 'block' }}
              />
            )}

            {isFile && (
              <Box
                component="a"
                href={attachmentUrl}
                target="_blank"
                rel="noreferrer"
                download={message.attachment_name || undefined}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 1,
                  py: 0.85,
                  mb: message.body ? 0.75 : 0,
                  borderRadius: 1.5,
                  textDecoration: 'none',
                  bgcolor: mine ? 'rgba(255,255,255,0.16)' : TEAM_CHAT_THEME.mutedAlt,
                  color: 'inherit',
                  maxWidth: 300,
                }}
              >
                <InsertDriveFileOutlinedIcon sx={{ fontSize: 22, flexShrink: 0 }} />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography noWrap sx={{ fontSize: '0.8rem', fontWeight: 600 }}>
                    {message.attachment_name || 'Arquivo'}
                  </Typography>
                  <Typography sx={{ fontSize: '0.68rem', opacity: 0.75 }}>
                    {formatFileSize(message.attachment_size)}
                  </Typography>
                </Box>
                <DownloadIcon sx={{ fontSize: 18, flexShrink: 0, opacity: 0.8 }} />
              </Box>
            )}

            {message.body && (
              <Typography component="div" sx={teamChatBubbleBodySx}>
                {message.body}
              </Typography>
            )}
          </>
        )}

        <Box sx={teamChatBubbleMetaSx(mine)}>
          {message.is_edited && !message.is_deleted && <span>editada</span>}
          <span>{formatMessageTime(message.created_at)}</span>
          {mine &&
            !message.is_deleted &&
            (readByAll ? (
              <DoneAllIcon sx={{ fontSize: 14, color: '#93C5FD', flexShrink: 0 }} />
            ) : (
              <CheckIcon sx={{ fontSize: 14, opacity: 0.7, flexShrink: 0 }} />
            ))}
        </Box>

        {!message.is_deleted && (
          <Box
            className="team-chat-actions"
            sx={{
              position: 'absolute',
              top: -6,
              ...(mine ? { left: -4, transform: 'translateX(-100%)' } : { right: -4, transform: 'translateX(100%)' }),
              display: 'flex',
              alignItems: 'center',
              gap: 0.1,
              opacity: 0,
              transition: 'opacity 0.15s ease',
              bgcolor: TEAM_CHAT_THEME.paper,
              borderRadius: 999,
              border: `1px solid ${TEAM_CHAT_THEME.borderSoft}`,
              boxShadow: '0 2px 8px rgba(15, 23, 42, 0.1)',
              px: 0.25,
              '.team-chat-message-row:hover &': { opacity: 1 },
            }}
          >
            <Tooltip title="Reagir">
              <IconButton size="small" onClick={(e) => setReactionAnchor(e.currentTarget)}>
                <AddReactionOutlinedIcon sx={{ fontSize: 16, color: TEAM_CHAT_THEME.slateFaint }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Responder">
              <IconButton size="small" onClick={() => onReply(message)}>
                <ReplyIcon sx={{ fontSize: 16, color: TEAM_CHAT_THEME.slateFaint }} />
              </IconButton>
            </Tooltip>
            <IconButton size="small" onClick={(e) => setMenuAnchor(e.currentTarget)}>
              <MoreHorizIcon sx={{ fontSize: 16, color: TEAM_CHAT_THEME.slateFaint }} />
            </IconButton>
          </Box>
        )}
      </Paper>

      {message.reactions.length > 0 && (
        <Box sx={{ display: 'flex', gap: 0.4, mt: 0.5, flexWrap: 'wrap' }}>
          {message.reactions.map((reaction) => (
            <Box
              key={reaction.emoji}
              onClick={() => onToggleReaction(message, reaction.emoji)}
              sx={teamChatReactionChipSx(reaction.user_ids.includes(currentUserId))}
            >
              <span>{reaction.emoji}</span>
              <span>{reaction.user_ids.length}</span>
            </Box>
          ))}
        </Box>
      )}

      <Menu
        anchorEl={reactionAnchor}
        open={Boolean(reactionAnchor)}
        onClose={() => setReactionAnchor(null)}
        PaperProps={{ sx: { borderRadius: 999, px: 0.5 } }}
      >
        <Box sx={{ display: 'flex', gap: 0.25 }}>
          {QUICK_REACTIONS.map((emoji) => (
            <IconButton
              key={emoji}
              size="small"
              onClick={() => {
                onToggleReaction(message, emoji);
                setReactionAnchor(null);
              }}
              sx={{ fontSize: '1.05rem' }}
            >
              {emoji}
            </IconButton>
          ))}
        </Box>
      </Menu>

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
        <MenuItem
          onClick={() => {
            onReply(message);
            setMenuAnchor(null);
          }}
          sx={{ fontSize: '0.85rem' }}
        >
          Responder
        </MenuItem>
        {message.body && (
          <MenuItem
            onClick={() => {
              void navigator.clipboard?.writeText(message.body || '');
              setMenuAnchor(null);
            }}
            sx={{ fontSize: '0.85rem' }}
          >
            Copiar texto
          </MenuItem>
        )}
        {canEdit && (
          <MenuItem
            onClick={() => {
              onEdit(message);
              setMenuAnchor(null);
            }}
            sx={{ fontSize: '0.85rem' }}
          >
            Editar
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            onDelete(message);
            setMenuAnchor(null);
          }}
          sx={{ fontSize: '0.85rem', color: TEAM_CHAT_THEME.cancelledText }}
        >
          Apagar
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default TeamChatMessageBubble;
